import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import RequireAuth from './components/RequireAuth.jsx';
import { BRAND } from './constants/brand.js';
import { joinRoom } from './api/socket.js';
import { trackPage } from './analytics.js';

/**
 * Колдонмонун бардык роуту.
 *
 * Система жабык (ТЗ 2.1) — SEO жок, ошондуктан бардык экран браузерде гана
 * жүктөлөт: ар бири өзүнчө чанк (`lazy`), баары `RequireAuth`тин артында.
 */
const LoginView = lazy(() => import('./views/auth/LoginView.jsx'));
const ProjectsView = lazy(() => import('./views/projects/ProjectsView.jsx'));
const ProjectView = lazy(() => import('./views/project/ProjectView.jsx'));
const ChapterView = lazy(() => import('./views/chapter/ChapterView.jsx'));
const GlossaryView = lazy(() => import('./views/glossary/GlossaryView.jsx'));
const HealthView = lazy(() => import('./views/health/HealthView.jsx'));
const ImportReviewView = lazy(() => import('./views/importReview/ImportReviewView.jsx'));
const SearchView = lazy(() => import('./views/search/SearchView.jsx'));
const SettingsView = lazy(() => import('./views/settings/SettingsView.jsx'));
const DemosView = lazy(() => import('./views/demos/DemosView.jsx'));

export default function App() {
  useSocketRooms();
  usePageViews();

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route path="/login" element={<Screen title="Вход"><LoginView /></Screen>} />
        <Route path="/demos" element={<Screen title="Демо по странам"><DemosView /></Screen>} />

        <Route element={<Protected />}>
          <Route path="/" element={<Screen title="Проекты"><ProjectsView /></Screen>} />
          <Route path="/settings" element={<Screen title="Настройки"><SettingsView /></Screen>} />
          <Route path="/project/:id" element={<Screen title="Проект"><ProjectView /></Screen>} />
          <Route path="/project/:id/glossary" element={<Screen title="Глоссарий"><GlossaryView /></Screen>} />
          <Route path="/project/:id/health" element={<Screen title="Здоровье книги"><HealthView /></Screen>} />
          <Route path="/project/:id/import" element={<Screen title="Проверка импорта"><ImportReviewView /></Screen>} />
          <Route path="/project/:id/search" element={<Screen title="Поиск по книге"><SearchView /></Screen>} />
          <Route path="/chapter/:id" element={<Screen title="Глава"><ChapterView /></Screen>} />
        </Route>

        {/* Жок дарек — долбоорлорго */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

/**
 * Сервер прогрессти долбоордун жана главанын бөлмөсүнө жиберет
 * (`socket/index.js`), ошондуктан кардар кайсы бетте турса, ошол бөлмөдө
 * болушу керек. Дарек — кайсы беттемин дегендин эң ишенимдүү булагы,
 * ошондуктан бөлмө ушул жерде бир эле жолу башкарылат.
 */
function useSocketRooms() {
  const { pathname } = useLocation();
  useEffect(() => {
    const match = pathname.match(/^\/(project|chapter)\/([^\/]+)/);
    return match ? joinRoom(match[1], match[2]) : undefined;
  }, [pathname]);
}

/** Ар бет алмашканда аналитикага (SPA — браузер өзү билбейт) */
function usePageViews() {
  const { pathname, search } = useLocation();
  useEffect(() => { trackPage(); }, [pathname, search]);
}

function Protected() {
  return <RequireAuth><Outlet /></RequireAuth>;
}

/** Браузердин өтмөгүндөгү аталыш (мурда ар бетте `<Head><title>` болчу) */
function Screen({ title, children }) {
  useEffect(() => {
    document.title = title ? `${title} — ${BRAND.name}` : BRAND.name;
  }, [title]);
  return children;
}

function Loading() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
      <CircularProgress />
    </Box>
  );
}
